import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { 
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "../components/ui/card";
import { fetchWithAuth, invalidateCache } from '../utils/api';
import { motion } from 'framer-motion';
import { ArrowLeft, Share2, Copy, UserPlus, Users, Award, Eye, Trash2, RefreshCw } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from "react-hot-toast";
import { UserAvatar } from '../components/UserAvatar';
import { AuthContext } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Add standardizeFormat utility function
const standardizeFormat = (format: string | undefined): string => {
  if (!format) return 'BP'; // Default to BP if format is undefined
  
  const upperFormat = format.toUpperCase();
  
  if (upperFormat === 'ABP' || upperFormat === 'BP') {
    return 'BP';
  }
  
  if (upperFormat === 'PDA' || upperFormat === 'AP3' || upperFormat === 'AP') {
    return 'AP';
  }
  
  return format;
};

/* eslint-disable @typescript-eslint/no-unused-vars */
// These interfaces are kept for reference and potential future use
interface User {
  id: number;
  username: string;
  email: string;
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface Allocation {
  user_id: number;
  role: string;
}
/* eslint-enable @typescript-eslint/no-unused-vars */

// Reserved for future implementation
interface RoundDetails {
  id: number;
  format: string;
  status: string;
  round_code: string;
  max_adjudicators: number;
  created_at: string;
  started_at?: string;
  is_moderator?: boolean;
  moderator?: {
    id: number;
    username: string;
  };
  participants?: Array<{
    id: number;
    user_id?: number;
    role?: string;
    user?: {
      id: number;
      username: string;
      email?: string;
      first_name?: string;
      last_name?: string;
    }
  }>;
  motion?: {
    id: number;
    text: string;
    theme?: {
      id: number;
      name: string;
    };
  };
}

interface UserForAllocation {
  id: number;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  selected_role?: string;
  is_moderator?: boolean;  
  is_admin?: boolean;      
}

interface Participant {
  id: number;
  user_id?: number; // Make sure this exists to differentiate from participant id
  username: string;
  role: string;
  is_ready: boolean;
  is_moderator?: boolean;
  user?: {
    id?: number; // Add this field to user object
    username: string;
    first_name?: string;
    last_name?: string;
  };
}

// Add this helper function outside of component
const getUserId = (): number | null => {
  try {
    const userDataStr = localStorage.getItem('user');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      return userData.id || null;
    }
    return null;
  } catch (err) {
    console.error("Error getting user ID:", err);
    return null;
  }
};

const RoundSetup: React.FC = () => {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const { pageTransition } = useTheme();
  const authContext = useContext(AuthContext);
  const user = authContext?.currentUser;
  const isAuthenticated = !!authContext?.currentUser;
  
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roundData, setRoundData] = useState<RoundDetails | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  
  // Participant state (moved from WaitingRoom)
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [showRoleAssignmentModal, setShowRoleAssignmentModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const latestParticipantsRef = useRef<Participant[]>([]);
  
  // Add state to track unassigned participants specifically
  const [unassignedParticipants, setUnassignedParticipants] = useState<Participant[]>([]);
  
  /* eslint-disable @typescript-eslint/no-unused-vars */
  // Keep this state for future role filtering functionality
  const [roleFilter, setRoleFilter] = useState("all");
  /* eslint-enable @typescript-eslint/no-unused-vars */
  
  // Store the latest data in refs to avoid losing it during refreshes
  const latestRoundDataRef = useRef<RoundDetails | null>(null);
  const latestAvailableUsersRef = useRef<UserForAllocation[]>([]);
  
  // Reference to track if the current user was a participant previously
  const wasParticipantRef = useRef<boolean>(false);
  
  // Manual refresh state
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  
  // Add state for AlertDialog management
  
  // Add state for AlertDialog management
  const [showKickAlert, setShowKickAlert] = useState(false);
  const [showRemoveAlert, setShowRemoveAlert] = useState(false);
  const [participantToKick, setParticipantToKick] = useState<Participant | null>(null);
  const [participantToRemove, setParticipantToRemove] = useState<Participant | null>(null);
  const [kickingParticipant, setKickingParticipant] = useState(false);
  const [removingParticipant, setRemovingParticipant] = useState(false);
  
  // Add state for search and filters
  
  // Define motion variants for framer-motion - MOVED HERE to prevent redeclaration
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } }
  };

  useEffect(() => {
    // Safe access to localStorage
    try {
      const adminStatus = localStorage.getItem("is_admin") === "true";
      setIsAdmin(adminStatus);
    } catch (err) {
      console.error("Error accessing localStorage:", err);
    }

    // Initial data fetches
    fetchRoundData(true);
    fetchParticipants(true);
    fetchAvailableUsers(true);
    
    // REDUCED POLLING INTERVALS - Much less aggressive polling
    // Only check round status every 30 seconds (reduced from 2-5 seconds)
    const statusInterval = setInterval(() => {
      checkRoundStatus();
    }, 30000);
    
    // Refresh participants every 60 seconds (reduced from 5 seconds)
    const participantsInterval = setInterval(() => {
      fetchParticipants(false);
    }, 60000);
    
    // General data refresh every 2 minutes (reduced from 10 seconds)
    const dataRefreshInterval = setInterval(() => {
      fetchRoundData(false);
      fetchAvailableUsers(false);
    }, 120000);
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(participantsInterval);
      clearInterval(dataRefreshInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId]);
  
  useEffect(() => {
    fetchAvailableUsers(true);
    fetchParticipants(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId]);

  const fetchRoundData = async (isInitialLoad: boolean) => {
    try {
      // Only show loading indicator on initial page load
      if (isInitialLoad) {
        setLoading(true);
      }
      
      setError("");
      
      console.log(`${isInitialLoad ? "Initial" : "Background"} round data fetch...`);
      
      let data: RoundDetails | null = null;
      let fetchSuccess = false;
      
      // Simplified list of endpoints - keeping only the most likely to work
      const endpoints = [
        `/api/rounds/${roundId}/details/`,
        `/api/rounds/${roundId}/`,
        `/api/admin/rounds/${roundId}/`
      ];
      
      // Try each endpoint until one works
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying endpoint: ${endpoint}`);
          const response = await fetchWithAuth(endpoint);
          
          if (response && response.id) {
            console.log(`Round data fetched successfully from ${endpoint}:`, response);
            
            // Ensure the round data has motion and round_code properly formatted
            const processedResponse = {
              ...response,
              round_code: response.round_code || response.code || 'LOADING',
              // Make sure motion is properly structured
              motion: {
                id: response.motion?.id || 0,
                text: response.motion?.text || response.motion_text || 'Motion not available',
                theme: {
                  id: response.motion?.theme?.id || 0,
                  name: response.motion?.theme?.name || response.theme || 'General'
                }
              }
            };
            
            data = processedResponse as RoundDetails;
            fetchSuccess = true;
            break;
          } else {
            console.log(`Endpoint ${endpoint} returned invalid data:`, response);
          }
        } catch (endpointErr) {
          console.log(`Endpoint ${endpoint} failed:`, endpointErr);
        }
      }
      
      if (fetchSuccess && data) {
        // Update state without triggering rerenders for minor changes
        setRoundData(prevData => {
          // Only update if data actually changed or no previous data
          if (!prevData || 
              (data && data.participants && prevData.participants && 
              JSON.stringify(prevData.participants) !== JSON.stringify(data.participants)) ||
              (data && prevData && prevData.status !== data.status)) {
            latestRoundDataRef.current = data;
            return data;
          }
          return prevData;
        });
        
        // Set moderator status
        if (data.is_moderator !== undefined) {
          setIsModerator(data.is_moderator);
        } else if (data.moderator && data.moderator.id === getUserId()) {
          setIsModerator(true);
        }
        
        // Fix the mixed operators issue by adding explicit parentheses
        const isUserAdmin = (localStorage.getItem('is_admin') === 'true') || (localStorage.getItem('is_staff') === 'true');
        setIsAdmin(isUserAdmin);
        
        return;
      }
      
      // If all endpoints failed but we have cached data, use that and don't show errors
      if (latestRoundDataRef.current && !isInitialLoad) {
        console.log("Using cached round data due to all endpoint failures");
        return;
      }
      
      // Only throw error for initial load with no cached data
      if (isInitialLoad && !latestRoundDataRef.current) {
        throw new Error("Failed to fetch round data from all endpoints");
      }
    } catch (err) {
      console.error("Error fetching round data:", err);
      
      // Only show error on initial load without cached data
      if (isInitialLoad && !latestRoundDataRef.current) {
        setError(err instanceof Error ? err.message : "Failed to load round data");
      }
      
      // Create minimal round data if we have none to prevent UI failure
      if (!latestRoundDataRef.current && roundId) {
        const minimalData: RoundDetails = {
          id: parseInt(roundId),
          format: "BP",
          status: "setup",
          round_code: "LOADING",
          max_adjudicators: 3,
          created_at: new Date().toISOString(),
          motion: {
            id: 0,
            text: "Loading motion...",
            theme: {
              id: 0,
              name: "Loading..."
            }
          },
          participants: []
        };
        
        setRoundData(minimalData);
        latestRoundDataRef.current = minimalData;
      }
    } finally {
      // Always turn off loading state after initial load
      if (isInitialLoad) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  };

  const fetchAvailableUsers = async (isInitialLoad: boolean = true) => {
    if (!roundId) return;
    
    try {
      if (!isInitialLoad) {
        console.log(`Background refresh of available users for round: ${roundId}`);
      }
      
      // Try multiple endpoints to get available users
      const endpoints = [
        `/api/round/${roundId}/available-users/`,
        `/api/rounds/${roundId}/available-users/`
      ];
      
      let data = null;
      
      for (const endpoint of endpoints) {
        try {
          if (!isInitialLoad) {
            console.log(`Trying to fetch available users from: ${endpoint}`);
          }
          data = await fetchWithAuth(endpoint);
          if (data) {
            if (!isInitialLoad) {
              console.log(`Successfully fetched available users from ${endpoint}`);
            }
            break;
          }
        } catch (err) {
          if (!isInitialLoad) {
            console.log(`Failed to fetch available users from ${endpoint}:`, err);
          }
        }
      }
      
      if (data && Array.isArray(data)) {
        setUnassignedParticipants(data);
      }
    } catch (err) {
      console.error("Error fetching available users:", err);
    }
  };

  // Function to check if the current user is still a participant in the round
  // This is a critical safety mechanism to ensure users are redirected if they're kicked
  // We use multiple methods to ensure the redirect happens:
  // 1. WebSocket force_redirect message (primary method)
  // 2. Checking participant list on each refresh (backup method)
  // 3. Notification-based redirect (fallback method)
  const checkRoundStatus = async () => {
    if (!roundId) return;
    
    try {
      // First check if round status has changed to active or completed
      let status = null;
      try {
        const data = await fetchWithAuth(`/api/round/${roundId}/status/`);
        if (data && data.status) {
          status = data.status.toLowerCase(); // Normalize status to lowercase
        }
      } catch (err) {
        console.log(`Status endpoint failed, using existing data`);
        if (roundData && roundData.status) {
          status = roundData.status.toLowerCase(); // Normalize existing status
        }
      }
      
      // Check if the round has been started (active) or completed
      if (status === 'active' || status === 'completed') {
        console.log(`Round status changed to ${status}, redirecting...`);
        
        // If we're already on the round setup page, redirect to the active round
        if (status === 'active') {
          pageTransition(() => {
            navigate(`/round/${roundId}`);
          });
        } 
        // If the round is completed, redirect to results
        else if (status === 'completed') {
          pageTransition(() => {
            navigate(`/round/${roundId}/results`);
          });
        }
        return;
      }
      
      // Next, check if the current user is still a participant in this round
      // This will detect if they've been kicked
      const userId = getUserId();
      if (!userId) return;
      
      // Check if we're still in the participants list
      const stillParticipant = participants.some(p => 
        (p.user_id === userId || (p.user && p.user.id === userId))
      ) || unassignedParticipants.some(p => 
        (p.user_id === userId || (p.user && p.user.id === userId))
      );
      
      // If we were previously a participant but now we're not, we've been kicked
      if (!stillParticipant && wasParticipantRef.current) {
        console.log('User appears to have been kicked from round, redirecting to dashboard');
        toast.error('You have been removed from this round by the moderator', {
          duration: 3000,
        });
        pageTransition(() => {
          navigate('/dashboard');
        });
        return;
      }
      
      // Update our reference of whether we're a participant
      wasParticipantRef.current = stillParticipant;
    } catch (err) {
      console.error("Error checking round status:", err);
    }
  };
  
  // Update the handleStartRound function to ensure proper redirection
const handleStartRound = async () => {
  if ((!isAdmin && !isModerator) || !roundId) return;
  
  try {
    // Try multiple endpoints for starting the round, including the new simpler one
    const startEndpoints = [
      `/api/round/${roundId}/simple-start/`,  // New reliable endpoint
      `/api/admin/rounds/${roundId}/start/`,
      `/api/rounds/${roundId}/start/`,
      `/api/round/${roundId}/start/`,
      `/round/${roundId}/start/`
    ];
    
    let success = false;
    let lastError = null;
    
    // Try each endpoint until one works
    for (const endpoint of startEndpoints) {
      try {
        console.log(`Trying to start round via endpoint: ${endpoint}`);
        await fetchWithAuth(endpoint, {
          method: 'POST'
        });
        success = true;
        toast.success('Round started successfully!');
        
        // Redirect to the active round page
        setTimeout(() => {
          pageTransition(() => {
            navigate(`/round/${roundId}`);
          });
        }, 500);
        
        break;
      } catch (err: any) {
        console.error(`Failed to start round via ${endpoint}:`, err);
        lastError = err;
        // Continue to next endpoint
      }
    }
    
    if (!success && lastError) {
      throw lastError;
    }
  } catch (err: any) {
    console.error("Error starting round:", err);
    setError(err.message || "Failed to start round");
    toast.error(err.message || "Failed to start round");
  }
};

  const handleBack = () => {
    pageTransition(() => {
      navigate("/dashboard");
    });
  };

  // Manual refresh function
  const handleManualRefresh = async () => {
    if (isManualRefreshing) return; // Prevent multiple simultaneous refreshes
    
    try {
      setIsManualRefreshing(true);
      console.log('Manual refresh triggered');
      
      // Refresh all data simultaneously
      await Promise.all([
        fetchRoundData(false),
        fetchParticipants(false),
        fetchAvailableUsers(false),
        checkRoundStatus()
      ]);
      
      toast.success("Data refreshed successfully", {
        duration: 2000,
      });
    } catch (error) {
      console.error('Error during manual refresh:', error);
      toast.error("Failed to refresh data", {
        duration: 3000,
      });
    } finally {
      setIsManualRefreshing(false);
    }
  };

  // Add safe filtering functions for participant roles
  const safeFilter = (participants: Participant[], roles: string[]) => {
    try {
      return participants.filter(p => 
        p.role && roles.includes(p.role)
      );
    } catch (err) {
      console.error("Error filtering participants:", err);
      return [];
    }
  };

  // Filter participants by role with safeguards
  const governmentSpeakers = safeFilter(participants, [
    'Prime Minister', 'Deputy Prime Minister', 'Government Whip', 'Member of Government'
  ]);
  
  const oppositionSpeakers = safeFilter(participants, [
    'Leader of Opposition', 'Deputy Leader of Opposition', 'Opposition Whip', 'Member of Opposition'
  ]);

  // Use safe object access for adjudicators
  const adjudicators = {
    chair: safeFilter(participants, ['Chair Adjudicator']),
    panelist: safeFilter(participants, ['Panelist']),
    trainee: safeFilter(participants, ['Trainee'])
  };
  
  const spectators = safeFilter(participants, ['Spectator']);

  // Helper function to get team capacity
  const getTeamCapacity = (team: Participant[]) => {
    try {
      const standardFormat = standardizeFormat(roundData?.format);
      const capacity = standardFormat === 'BP' ? 
        (team.length > 0 && team[0]?.role?.includes('Opening') ? 2 : 2) : 3;
      return `(${team.length}/${capacity})`;
    } catch (err) {
      console.error("Error in getTeamCapacity:", err);
      return `(${team.length}/?)`;
    }
  };

  // Helper to get BP-specific teams with safeguards
  const getBPTeams = () => {
    try {
      return {
        openingGov: participants.filter(p => ['Prime Minister', 'Deputy Prime Minister'].includes(p.role || '')),
        openingOpp: participants.filter(p => ['Leader of Opposition', 'Deputy Leader of Opposition'].includes(p.role || '')),
        closingGov: participants.filter(p => ['Government Whip', 'Member of Government'].includes(p.role || '')),
        closingOpp: participants.filter(p => ['Opposition Whip', 'Member of Opposition'].includes(p.role || ''))
      };
    } catch (err) {
      console.error("Error in getBPTeams:", err);
      return {
        openingGov: [],
        openingOpp: [],
        closingGov: [],
        closingOpp: []
      };
    }
  };

  // Add function to get available roles
  const getAvailableRoles = () => {
    // Get roles based on format
    const format = standardizeFormat(roundData?.format);
    
    let allRoles = [];
    
    if (format === 'BP') {
      allRoles = [
        'Prime Minister',
        'Deputy Prime Minister',
        'Leader of Opposition',
        'Deputy Leader of Opposition',
        'Government Whip',
        'Member of Government',
        'Opposition Whip',
        'Member of Opposition',
        'Chair Adjudicator',
        'Panelist',
        'Trainee',
        'Spectator'
      ];
    } else { // Asian Parliamentary
      allRoles = [
        'Prime Minister',
        'Deputy Prime Minister',
        'Government Whip',
        'Leader of Opposition',
        'Deputy Leader of Opposition',
        'Opposition Whip',
        'Chair Adjudicator',
        'Panelist',
        'Trainee',
        'Spectator'
      ];
    }
    
    // Filter out roles that are already assigned
    const teams = getBPTeams();
    const assignedRoles = participants.map(p => p.role);
    
    // Check if Chair Adjudicator is already assigned
    const chairAssigned = adjudicators.chair.length > 0;
    
    // Check if max panelists have been reached
    const maxPanelists = roundData?.max_adjudicators ? roundData.max_adjudicators - 1 : 2; // -1 for the chair
    const panelistCount = adjudicators.panelist.length;
    const panelistsFull = panelistCount >= maxPanelists;
    
    // Filter out roles that are already assigned
    // For BP format
    if (format === 'BP') {
      if (teams.openingGov.find(p => p.role === 'Prime Minister')) {
        allRoles = allRoles.filter(role => role !== 'Prime Minister');
      }
      if (teams.openingGov.find(p => p.role === 'Deputy Prime Minister')) {
        allRoles = allRoles.filter(role => role !== 'Deputy Prime Minister');
      }
      if (teams.openingOpp.find(p => p.role === 'Leader of Opposition')) {
        allRoles = allRoles.filter(role => role !== 'Leader of Opposition');
      }
      if (teams.openingOpp.find(p => p.role === 'Deputy Leader of Opposition')) {
        allRoles = allRoles.filter(role => role !== 'Deputy Leader of Opposition');
      }
      if (teams.closingGov.find(p => p.role === 'Government Whip')) {
        allRoles = allRoles.filter(role => role !== 'Government Whip');
      }
      if (teams.closingGov.find(p => p.role === 'Member of Government')) {
        allRoles = allRoles.filter(role => role !== 'Member of Government');
      }
      if (teams.closingOpp.find(p => p.role === 'Opposition Whip')) {
        allRoles = allRoles.filter(role => role !== 'Opposition Whip');
      }
      if (teams.closingOpp.find(p => p.role === 'Member of Opposition')) {
        allRoles = allRoles.filter(role => role !== 'Member of Opposition');
      }
    } else { // AP format
      if (assignedRoles.includes('Prime Minister')) {
        allRoles = allRoles.filter(role => role !== 'Prime Minister');
      }
      if (assignedRoles.includes('Deputy Prime Minister')) {
        allRoles = allRoles.filter(role => role !== 'Deputy Prime Minister');
      }
      if (assignedRoles.includes('Government Whip')) {
        allRoles = allRoles.filter(role => role !== 'Government Whip');
      }
      if (assignedRoles.includes('Leader of Opposition')) {
        allRoles = allRoles.filter(role => role !== 'Leader of Opposition');
      }
      if (assignedRoles.includes('Deputy Leader of Opposition')) {
        allRoles = allRoles.filter(role => role !== 'Deputy Leader of Opposition');
      }
      if (assignedRoles.includes('Opposition Whip')) {
        allRoles = allRoles.filter(role => role !== 'Opposition Whip');
      }
    }
    
    // Always filter out Chair Adjudicator if assigned
    if (chairAssigned) {
      allRoles = allRoles.filter(role => role !== 'Chair Adjudicator');
    }
    
    // Only filter out Panelist if max has been reached
    if (panelistsFull) {
      allRoles = allRoles.filter(role => role !== 'Panelist');
    }
    
    // Always include Trainee and Spectator
    
    return allRoles;
  };

  // Update assignRole to not use allocations state
const assignRole = async (participantId: number, role: string) => {
  try {
    // Find the participant to get the correct user_id
    const participant = unassignedParticipants.find(p => p.id === participantId);
    
    if (!participant) {
      toast.error("Participant not found");
      return;
    }
    
    // Determine the correct user ID
    // First try the specific user_id field, then try user.id, finally fallback to participant id
    const userId = participant.user_id || (participant.user?.id) || participantId;
    
    console.log(`Assigning role ${role} to user ID ${userId} for participant ${participantId}`);
    
    // Send the user ID (not participant ID) in the allocation
    await fetchWithAuth(`/api/round/${roundId}/allocate/`, {
      method: "POST",
      body: JSON.stringify({ 
        allocations: [{ user_id: userId, role }] 
      })
    });
    
    // After successful role assignment, invalidate any API cache for participant endpoints
    // Import from utils/api if not already imported
    
    // Invalidate participant cache to force fresh data
    invalidateCache(`/api/round/${roundId}/participants/`);
    
    // Refresh participants list
    fetchParticipants(true);
    setShowRoleAssignmentModal(false);
    setSelectedParticipant(null);
    
    toast.success(`Role ${role} assigned successfully`);
  } catch (err: any) {
    console.error("Error assigning role:", err);
    toast.error("Failed to assign role. Please try again.");
  }
};

  // Add this function to redirect users after being kicked
  const redirectToDashboard = () => {
    window.location.href = '/dashboard';
  };

  // Add function to kick participant
  const kickParticipant = async (participantId: number) => {
    // Find the participant to kick
    const participant = unassignedParticipants.find(p => p.id === participantId) || 
                       participants.find(p => p.id === participantId);
                       
    if (!participant) {
      toast.error("Participant not found");
      return;
    }
    
    setParticipantToKick(participant);
    setShowKickAlert(true);
  };

  // Add function to confirm kick participant
  const confirmKickParticipant = async () => {
    if (!participantToKick) return;
    
    try {
      setKickingParticipant(true);
      
      console.log(`Kicking participant ${participantToKick.id}`);
      
      // Get the user's ID for WebSocket notification
      const userId = participantToKick.user_id || (participantToKick.user?.id);
      
      if (!userId) {
        toast.error("Could not determine user ID");
        return;
      }
      
      // Delete the participant record with kick=true parameter
      const response = await fetchWithAuth(`/api/round/participant/${participantToKick.id}/?kick=true`, {
        method: "DELETE"
      });
      
      console.log(`Kick response:`, response);
      
      // The backend will handle sending WebSocket notifications
      // We don't need to send a separate WebSocket message here
      
      toast.success("Participant kicked successfully");
      
      // Refresh the participants list
      await fetchParticipants(true);
      await fetchAvailableUsers(true);
    } catch (error) {
      console.error("Error kicking participant:", error);
      toast.error("Failed to kick participant");
    } finally {
      setKickingParticipant(false);
      setShowKickAlert(false);
      setParticipantToKick(null);
    }
  };

  // Add function to remove participant
  const removeParticipant = async (participantId: number) => {
    // Find the participant to remove
    const participant = participants.find(p => p.id === participantId);
    if (!participant) {
      toast.error("Participant not found");
      return;
    }

    setParticipantToRemove(participant);
    setShowRemoveAlert(true);
  };

  // Add function to confirm remove participant
  const confirmRemoveParticipant = async () => {
    if (!participantToRemove) return;
    
    try {
      setRemovingParticipant(true);

      // Remove their role but keep them in the round as available
      // Don't pass kick=true parameter to indicate this is just a role removal
      await fetchWithAuth(`/api/round/participant/${participantToRemove.id}/`, {
        method: "DELETE"
      });
      
      // Important: Refresh both participants AND available users lists
      await fetchParticipants(true);
      await fetchAvailableUsers(true);
      
      // Invalidate any cached data
      invalidateCache(`/api/round/${roundId}/participants/`);
      invalidateCache(`/api/round/${roundId}/available-users/`);
      
      toast.success("Participant removed from role successfully");
    } catch (err: any) {
      console.error("Error removing participant:", err);
      toast.error("Failed to remove participant from role");
    } finally {
      setRemovingParticipant(false);
      setShowRemoveAlert(false);
      setParticipantToRemove(null);
    }
  };

  // Create participant card component
  const ParticipantCard = ({ participant, isAvailable = false }: { participant: Participant, isAvailable?: boolean }) => {
    return (
      <motion.div 
        variants={itemVariants}
        className="flex items-center justify-between p-3 mb-2 bg-gray-700 bg-opacity-30 rounded-lg"
      >
        <div className="flex items-center">
          <UserAvatar username={participant.username} size="sm" className="mr-3" />
          <div>
            <p className="font-medium dark:text-white light:text-gray-800">
              {participant.user?.username || participant.username || "Unknown User"}
            </p>
            <p className="text-xs text-gray-400">{isAvailable ? "Waiting for role assignment" : (participant.role || "Unknown Role")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {participant.is_ready && !isAvailable && (
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
          )}
          
          {/* Admin controls */}
          {(isAdmin || isModerator) && (
            <div className="flex gap-2">
              {isAvailable ? (
                <>
                  {/* For Available Participants */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedParticipant(participant);
                      setAvailableRoles(getAvailableRoles());
                      setShowRoleAssignmentModal(true);
                    }}
                    className="text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900"
                  >
                    Assign Role
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      kickParticipant(participant.id);
                    }}
                    className="text-red-500 hover:bg-red-100 dark:hover:bg-red-900"
                  >
                    Kick
                  </Button>
                </>
              ) : (
                <>
                  {/* For participants with roles */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeParticipant(participant.id);
                    }}
                    className="text-red-500 hover:text-red-700"
                    aria-label="Remove from role"
                  >
                    <Trash2 size={16} />
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  // Render BP teams function
  const renderBPTeams = () => {
    const teams = getBPTeams();
    const standardFormat = standardizeFormat(roundData?.format);
    
    if (standardFormat !== 'BP') {
      return null; // Only render for BP format
    }
    
    return (
      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <h3 className="text-md font-medium text-blue-400 mb-2">
            Opening Government {getTeamCapacity(teams.openingGov)}
          </h3>
          <div className="border border-blue-900 rounded-lg p-2 bg-blue-900 bg-opacity-10">
            {teams.openingGov.map(p => (
              <ParticipantCard key={p.id} participant={p} isAvailable={false} />
            ))}
            {teams.openingGov.length < 2 && (
              <div className="flex items-center justify-center p-6 text-gray-500">
                <UserPlus size={20} className="mr-2" />
                Waiting for speakers...
              </div>
            )}
          </div>
          
          <h3 className="text-md font-medium text-blue-400 mt-4 mb-2">
            Closing Government {getTeamCapacity(teams.closingGov)}
          </h3>
          <div className="border border-blue-900 rounded-lg p-2 bg-blue-900 bg-opacity-10">
            {teams.closingGov.map(p => (
              <ParticipantCard key={p.id} participant={p} isAvailable={false} />
            ))}
            {teams.closingGov.length < 2 && (
              <div className="flex items-center justify-center p-6 text-gray-500">
                <UserPlus size={20} className="mr-2" />
                Waiting for speakers...
              </div>
            )}
          </div>
        </div>
        
        <div className="space-y-4">
          <h3 className="text-md font-medium text-red-400 mb-2">
            Opening Opposition {getTeamCapacity(teams.openingOpp)}
          </h3>
          <div className="border border-red-900 rounded-lg p-2 bg-red-900 bg-opacity-10">
            {teams.openingOpp.map(p => (
              <ParticipantCard key={p.id} participant={p} isAvailable={false} />
            ))}
            {teams.openingOpp.length < 2 && (
              <div className="flex items-center justify-center p-6 text-gray-500">
                <UserPlus size={20} className="mr-2" />
                Waiting for speakers...
              </div>
            )}
          </div>
          
          <h3 className="text-md font-medium text-red-400 mt-4 mb-2">
            Closing Opposition {getTeamCapacity(teams.closingOpp)}
          </h3>
          <div className="border border-red-900 rounded-lg p-2 bg-red-900 bg-opacity-10">
            {teams.closingOpp.map(p => (
              <ParticipantCard key={p.id} participant={p} isAvailable={false} />
            ))}
            {teams.closingOpp.length < 2 && (
              <div className="flex items-center justify-center p-6 text-gray-500">
                <UserPlus size={20} className="mr-2" />
                Waiting for speakers...
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  // Render AP teams function
  const renderAPTeams = () => {
    return (
      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-md font-medium text-blue-500 mb-2">
            Government {getTeamCapacity(governmentSpeakers)}
          </h3>
          <div className="border border-blue-900 rounded-lg p-2 bg-blue-900 bg-opacity-10">
            {governmentSpeakers.map(p => (
              <ParticipantCard key={p.id} participant={p} isAvailable={false} />
            ))}
            {governmentSpeakers.length < 3 && (
              <div className="flex items-center justify-center p-6 text-gray-500">
                <UserPlus size={20} className="mr-2" />
                Waiting for speakers...
              </div>
            )}
          </div>
        </div>
        
        <div>
          <h3 className="text-md font-medium text-red-500 mb-2">
            Opposition {getTeamCapacity(oppositionSpeakers)}
          </h3>
          <div className="border border-red-900 rounded-lg p-2 bg-red-900 bg-opacity-10">
            {oppositionSpeakers.map(p => (
              <ParticipantCard key={p.id} participant={p} isAvailable={false} />
            ))}
            {oppositionSpeakers.length < 3 && (
              <div className="flex items-center justify-center p-6 text-gray-500">
                <UserPlus size={20} className="mr-2" />
                Waiting for speakers...
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  // Add the fetchParticipants function directly into RoundSetup
  const fetchParticipants = async (isInitialLoad: boolean = false) => {
    if (!roundId) return;
    
    try {
      // Only show loading on initial load
      if (isInitialLoad) {
        setLoading(true);
      }
      
      let data: any[] = [];
      let success = false;
      
      // Define endpoints to try in order with more variations
      const endpoints = [
        `/api/round/${roundId}/participants/`,
        `/api/rounds/${roundId}/participants/`
      ];
      
      // Try each endpoint in sequence
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying to fetch participants from endpoint: ${endpoint}`);
          const response = await fetchWithAuth(endpoint);
          if (response) {
            data = response;
            success = true;
            console.log(`Successfully fetched participants from ${endpoint}`);
            break;
          }
        } catch (err) {
          console.log(`Failed to fetch participants from ${endpoint}:`, err);
        }
      }
      
      // If all endpoints failed but we have cached data, use that and don't show errors
      if (!success) {
        if (isInitialLoad && participants.length === 0) {
          // Only log error on initial load with no cached data
          console.log("Could not fetch participants from any endpoint");
        } else {
          console.log("Using cached participant data");
          return; // Keep using existing data
        }
      }
      
      if (success && Array.isArray(data)) {
        // Process participants with validation
        const mappedParticipants = data.map((p: any) => ({
          id: p.id,
          user_id: p.user_id || p.user?.id || null, // Ensure user_id is properly set
          username: p.user?.username || "Unknown User",
          role: p.role || null,
          is_ready: p.status === 'ready',
          is_moderator: false, // Will be updated if needed
          user: {
            id: p.user?.id || null, // Make sure user.id is captured
            username: p.user?.username || "Unknown User",
            first_name: p.user?.first_name || "",
            last_name: p.user?.last_name || ""
          }
        }));
        
        // Separate assigned and unassigned participants
        const assigned = mappedParticipants.filter(p => p.role !== null);
        const unassigned = mappedParticipants.filter(p => p.role === null);
        
        // Update assigned participants state
        setParticipants(assigned);
        
        // Update unassigned participants state
        setUnassignedParticipants(unassigned);
        
        setError("");
      } else if (success && data) {
        console.warn("Participants data is not an array:", data);
        // Don't update state if we received invalid data format
      }
    } catch (outerErr) {
      console.error("Error fetching participants:", outerErr);
      
      // Only show error on initial load with no cached data
      if (isInitialLoad && participants.length === 0) {
        setError("Failed to load participants. Please refresh the page.");
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Safe rendering with minimal loading indicator for initial load only
  if (initialLoad && loading && !roundData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Return the UI component
  return (
    <motion.div 
      className="p-4"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
    >
      <Card className="w-full max-w-5xl mx-auto shadow-lg rounded-lg transition-colors duration-200 dark:bg-gray-800 light:bg-white">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleBack}
              className="mr-2"
            >
              <ArrowLeft size={16} />
            </Button>
            <div>
              <CardTitle className="text-2xl font-bold dark:text-white light:text-gray-800">
                Round Setup
              </CardTitle>
              <CardDescription className="text-sm">
                Configure participants and roles
                {roundData?.motion && (
                  <span className="ml-1 font-medium text-blue-500">
                    • {standardizeFormat(roundData.format)} Format
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
          
          {/* Manual Refresh Button */}
          <div className="flex items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              disabled={isManualRefreshing}
              className="flex items-center space-x-2"
            >
              <RefreshCw 
                size={16} 
                className={`${isManualRefreshing ? 'animate-spin' : ''}`} 
              />
              <span>Refresh</span>
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {error && (
            <motion.div 
              variants={itemVariants}
              className="mb-6 p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg text-red-500"
            >
              {error}
            </motion.div>
          )}
          
          <motion.div variants={itemVariants} className="space-y-6">
            {/* Motion Display */}
            {roundData?.motion && (
              <motion.div 
                variants={itemVariants}
                className="p-4 mb-4 rounded-lg border-2 border-blue-500 bg-blue-900 bg-opacity-10"
              >
                <h3 className="text-lg font-semibold dark:text-white light:text-gray-800 mb-2">
                  Motion
                </h3>
                <p className="dark:text-white light:text-gray-800 font-medium">
                  {roundData.motion.text}
                </p>
                {roundData.motion.theme && (
                  <p className="text-sm text-blue-500 mt-1">
                    {roundData.motion.theme.name}
                  </p>
                )}
              </motion.div>
            )}
            
            {/* Share Round Code */}
            {roundData && roundData.round_code && (
              <motion.div 
                variants={itemVariants}
                className="p-4 mb-4 rounded-lg border-2 border-green-500 bg-green-900 bg-opacity-10"
              >
                <h3 className="text-lg font-semibold dark:text-white light:text-gray-800 mb-2 flex items-center">
                  <Share2 size={18} className="mr-2" />
                  Share Round
                </h3>
                <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-2">
                  Share this code with participants to join the round:
                </p>
                <div className="flex items-center space-x-2">
                  <div className="p-3 bg-gray-700 bg-opacity-30 rounded-lg text-center flex-1">
                    <p className="text-xl font-bold tracking-widest dark:text-white light:text-gray-800">
                      {roundData.round_code}
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => {
                      navigator.clipboard.writeText(roundData.round_code);
                      toast.success("Round code copied to clipboard");
                    }}
                    className="flex items-center space-x-1"
                  >
                    <Copy size={14} className="mr-1" />
                    Copy
                  </Button>
                </div>
              </motion.div>
            )}
            
            {/* Participants Display (Moved from WaitingRoom component) */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold dark:text-white light:text-gray-800">
                  Participants & Roles
                </h2>
              </div>
              
              {loading && participants.length === 0 && unassignedParticipants.length === 0 ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg text-red-500">
                  <h3 className="text-lg font-semibold mb-2">Error</h3>
                  <p>{error}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fetchParticipants(true)} 
                    className="mt-2 text-red-500 border-red-500"
                  >
                    Try Again
                  </Button>
                </div>
              ) : (participants.length === 0 && unassignedParticipants.length === 0) ? (
                <div className="p-4 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-500 mb-2">No Participants Yet</h3>
                  <p className="text-blue-500">
                    Share the round code with others so they can join this round.
                  </p>
                </div>
              ) : (
                <>
                  {/* Available Participants Section */}
                  {unassignedParticipants.length > 0 && (
                    <motion.div variants={itemVariants} className="space-y-4 mb-8">
                      <h2 className="text-xl font-bold flex items-center dark:text-white light:text-gray-800">
                        <UserPlus className="mr-2" size={20} />
                        Available Participants ({unassignedParticipants.length})
                      </h2>
                      
                      <div className="border border-amber-600 rounded-lg p-4 bg-amber-900 bg-opacity-10">
                        <p className="text-amber-500 text-sm mb-4">
                          These participants need to be assigned roles before the round can start
                        </p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {unassignedParticipants.map(participant => (
                            <ParticipantCard key={participant.id} participant={participant} isAvailable />
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                    
                  {/* Debaters Section */}
                  <motion.div variants={itemVariants} className="space-y-4">
                    <h2 className="text-xl font-bold flex items-center dark:text-white light:text-gray-800">
                      <Users className="mr-2" size={20} />
                      Debaters
                    </h2>
                    
                    {standardizeFormat(roundData?.format) === 'BP' ? (
                      renderBPTeams()
                    ) : (
                      renderAPTeams()
                    )}
                  </motion.div>
                  
                  {/* Adjudicators Section */}
                  <motion.div variants={itemVariants} className="space-y-4">
                    <h2 className="text-xl font-bold flex items-center dark:text-white light:text-gray-800">
                      <Award className="mr-2" size={20} />
                      Adjudicators
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h3 className="text-md font-medium text-purple-500 mb-2">
                          Chair Adjudicator ({adjudicators.chair.length}/1)
                        </h3>
                        <div className="border border-purple-900 rounded-lg p-2 bg-purple-900 bg-opacity-10">
                          {adjudicators.chair.map(p => (
                            <ParticipantCard key={p.id} participant={p} isAvailable={false} />
                          ))}
                          {adjudicators.chair.length === 0 && (
                            <div className="flex items-center justify-center p-6 text-gray-500">
                              <UserPlus size={20} className="mr-2" />
                              Waiting for chair adjudicator...
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-md font-medium text-purple-500 mb-2">
                          Panelists ({adjudicators.panelist.length}/2)
                        </h3>
                        <div className="border border-purple-900 rounded-lg p-2 bg-purple-900 bg-opacity-10">
                          {adjudicators.panelist.map(p => (
                            <ParticipantCard key={p.id} participant={p} isAvailable={false} />
                          ))}
                          {adjudicators.panelist.length < 1 && (
                            <div className="flex items-center justify-center p-6 text-gray-500">
                              <UserPlus size={20} className="mr-2" />
                              Waiting for panelists...
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-md font-medium text-purple-500 mb-2">
                          Trainees ({adjudicators.trainee.length})
                        </h3>
                        <div className="border border-purple-900 rounded-lg p-2 bg-purple-900 bg-opacity-10 min-h-[120px]">
                          {adjudicators.trainee.map(p => (
                            <ParticipantCard key={p.id} participant={p} isAvailable={false} />
                          ))}
                          {adjudicators.trainee.length === 0 && (
                            <div className="flex items-center justify-center p-6 text-gray-500">
                              <span className="text-sm">No trainees</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  
                  {/* Spectators Section */}
                  <motion.div variants={itemVariants} className="space-y-4">
                    <h2 className="text-xl font-bold flex items-center dark:text-white light:text-gray-800">
                      <Eye className="mr-2" size={20} />
                      Spectators ({spectators.length})
                    </h2>
                    
                    <div className="border border-gray-700 rounded-lg p-2 min-h-[100px]">
                      {spectators.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {spectators.map(p => (
                            <ParticipantCard key={p.id} participant={p} isAvailable={false} />
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center p-6 text-gray-500">
                          <span className="text-sm">No spectators</span>
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Start Round Button - only one instance */}
                  {(isAdmin || isModerator) && (
                    <motion.div variants={itemVariants} className="pt-4 mt-4 border-t border-gray-700">
                      <Button 
                        onClick={handleStartRound}
                        className="w-full bg-green-600 hover:bg-green-700 flex items-center justify-center"
                        disabled={
                          // Add check for unassigned participants
                          unassignedParticipants.length > 0 ||
                          // At least 1 chair adjudicator required
                          adjudicators.chair.length === 0 ||
                          // Format-specific speaker requirements
                          (standardizeFormat(roundData?.format) === 'BP' ? 
                            (getBPTeams().openingGov.length < 2 || 
                             getBPTeams().openingOpp.length < 2 ||
                             getBPTeams().closingGov.length < 2 ||
                             getBPTeams().closingOpp.length < 2) : 
                            (governmentSpeakers.length < 3 || oppositionSpeakers.length < 3)
                          )
                        }
                      >
                        <span className="mr-2">⏵</span> Start Round
                      </Button>
                      {unassignedParticipants.length > 0 && (
                        <p className="text-sm text-center text-amber-500 mt-2">
                          Assign roles to all participants before starting the round
                        </p>
                      )}
                    </motion.div>
                  )}
                </>
              )}
              
              {/* Role assignment modal */}
              {showRoleAssignmentModal && selectedParticipant && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full">
                    <h3 className="text-lg font-bold mb-4 dark:text-white">
                      Assign Role to {selectedParticipant.username}
                    </h3>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {availableRoles.map(role => (
                        <div
                          key={role}
                          onClick={() => {
                            assignRole(selectedParticipant.id, role);
                          }}
                          className="p-3 border rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          {role}
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="outline"
                        onClick={() => setShowRoleAssignmentModal(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </CardContent>
      </Card>
      
      {/* Kick Participant Confirmation Dialog */}
      <AlertDialog open={showKickAlert} onOpenChange={setShowKickAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kick Participant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to kick <strong>{participantToKick?.username}</strong>? 
              They will be removed from the round and redirected to the dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={kickingParticipant} onClick={() => { setShowKickAlert(false); setParticipantToKick(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmKickParticipant}
              disabled={kickingParticipant}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {kickingParticipant ? "Kicking..." : "Kick Participant"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Remove Participant Confirmation Dialog */}
      <AlertDialog open={showRemoveAlert} onOpenChange={setShowRemoveAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Participant from Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{participantToRemove?.username}</strong> from their role? 
              They will be moved back to the available participants list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingParticipant} onClick={() => { setShowRemoveAlert(false); setParticipantToRemove(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmRemoveParticipant}
              disabled={removingParticipant}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {removingParticipant ? "Removing..." : "Remove from Role"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default RoundSetup;